import {
  Background,
  Controls,
  MarkerType,
  ReactFlow,
  useEdgesState,
  useNodesInitialized,
  useNodesState,
  useReactFlow,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import React, { useContext, useEffect, useRef } from 'react';
import { Card } from '@mui/material';
import { useParams } from 'react-router';

import { ScenarioDiagramContext } from '../../../state/Context';
import { getLayoutedElements } from '../../../utils/layoutUtil';
import {
  useScenarioPatientCategoryMapQuery,
  useScenarioUnitMapQuery,
} from '../../../network/queries';
import { Loader } from '../../common';

import UnitNode from './UnitNode';
import JourneyEdge from './JourneyEdge';

function ScenarioFlow() {
  // Load unit map and patient categories for base scenario
  const { scenarioId } = useParams();
  const { isLoading: isLoadingUnitMap, data: unitMap } =
    useScenarioUnitMapQuery(scenarioId);
  const { isLoading: isLoadingPatientCategoryMap, data: patientCategoryMap } =
    useScenarioPatientCategoryMapQuery(scenarioId);

  const { fitView } = useReactFlow();
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);

  const initialized = useNodesInitialized();
  const hasLayout = useRef(false);

  // Update nodes and edges with data
  useEffect(() => {
    if (!isLoadingUnitMap && !isLoadingPatientCategoryMap) {
      // Map units to node objects
      const nodes = unitMap.map((unit, index) => ({
        id: unit.name, // TODO: patient categories to reference units by ID
        type: 'unit',
        position: { x: 0, y: index * 100 },
        data: { unit },
      }));

      // Map patient categories to edges
      const edges = patientCategoryMap.flatMap((patientCategory) =>
        patientCategory.journey.flatMap((journey) =>
          journey.options.map((option) => {
            const source = journey.start_unit;
            const target = option.id;
            return {
              id: `${patientCategory.id}_${source}_${target}`,
              type: 'journey',
              source,
              target,
              markerEnd: {
                type: MarkerType.ArrowClosed,
                width: 10,
                height: 10,
              },
              data: { patientCategory, journey, option },
            };
          }),
        ),
      );

      setNodes(nodes);
      setEdges(edges);
      hasLayout.current = false;
    }
  }, [
    isLoadingUnitMap,
    unitMap,
    isLoadingPatientCategoryMap,
    patientCategoryMap,
  ]);

  // Apply layout once nodes are initialized
  useEffect(() => {
    if (initialized && !hasLayout.current && nodes.length > 0) {
      const layouted = getLayoutedElements(nodes, edges);
      setNodes([...layouted.nodes]);
      setEdges([...layouted.edges]);
      hasLayout.current = true;
      setTimeout(() => fitView(), 50);
    }
  }, [initialized]);

  const { setState } = useContext(ScenarioDiagramContext);

  const onNodeClick = (_, node) =>
    setState((prev) => ({
      ...prev,
      open: true,
      data: node.data,
    }));

  return (
    <Card sx={{ height: '100%' }}>
      {isLoadingUnitMap || isLoadingPatientCategoryMap ? (
        <Loader />
      ) : (
        <ReactFlow
          nodes={nodes}
          edges={edges}
          nodeTypes={{ unit: UnitNode }}
          edgeTypes={{ journey: JourneyEdge }}
          nodesDraggable={false}
          nodesConnectable={false}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          onNodeClick={onNodeClick}
          fitView
        >
          <Background />
          <Controls />
        </ReactFlow>
      )}
    </Card>
  );
}

export default ScenarioFlow;
